import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { 
  Cloud, 
  LayoutDashboard, 
  Settings, 
  FileText, 
  MessageSquare, 
  LogOut, 
  User,
  ChevronDown,
  CreditCard,
  Shield,
  Bell,
  Globe
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

// Wolkenhelden Logo URL
const logoUrl = '/images/logo.png';

export const PortalLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  // Theme state
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') || 'system';
    }
    return 'system';
  });

  // Apply theme
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    
    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      root.classList.add(systemTheme);
    } else {
      root.classList.add(theme);
    }
    
    localStorage.setItem('theme', theme);
  }, [theme]);

  const isActive = (path) => {
    if (path === '/portal' || path === '/app') {
      return location.pathname === '/portal' || location.pathname === '/app';
    }
    return location.pathname === path || location.pathname === path.replace('/portal', '/app');
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Helden-Zentrale', path: '/portal' },
    { icon: Cloud, label: 'Meine Wolken', path: '/portal/wolken' },
    { icon: Globe, label: 'Meine Domains', path: '/portal/domains' },
    { icon: FileText, label: 'Helden-Rechnungen', path: '/portal/rechnungen' },
    { icon: MessageSquare, label: 'Helden-Support', path: '/portal/support' },
    { icon: Shield, label: 'Datenschutz (AVV)', path: '/portal/avv' },
  ];

  const handleLogout = () => {
    logout();
    toast.success('Erfolgreich ausgeloggt');
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-muted/30" data-testid="portal-layout">
      {/* Portal Header */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src={logoUrl} 
              alt="Wolkenhelden Logo"
              className="h-8 w-auto"
            />
            <span className="font-display text-xl font-semibold">Wolkenhelden</span>
            <span className="ml-2 rounded-full bg-gradient-to-r from-primary/20 to-blue-500/20 px-2 py-0.5 text-xs font-medium text-primary border border-primary/20">
              🦸 Helden-Portal
            </span>
          </Link>
          
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="hidden sm:block text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Zur Website
            </Link>
            
            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-button">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-primary to-blue-600">
                    <User className="h-4 w-4 text-white" />
                  </div>
                  <span className="hidden sm:block max-w-[120px] truncate text-sm font-medium">
                    {user?.name || user?.email?.split('@')[0] || 'Benutzer'}
                  </span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="end" 
                className="w-64 bg-background/95 backdrop-blur-xl border shadow-xl"
                sideOffset={8}
              >
                {/* User Info Header */}
                <div className="px-3 py-3 bg-gradient-to-r from-primary/10 to-blue-500/10 rounded-t-md -mt-1 -mx-1 mb-1">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-primary to-blue-600 shadow-md">
                      <User className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{user?.name || 'Helden-Account'}</p>
                      <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                    </div>
                  </div>
                </div>
                
                <DropdownMenuSeparator />
                
                {/* Navigation */}
                <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                  Navigation
                </DropdownMenuLabel>
                <DropdownMenuItem asChild>
                  <Link to="/portal" className="cursor-pointer">
                    <LayoutDashboard className="mr-2 h-4 w-4 text-primary" />
                    Helden-Zentrale
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/portal/wolken" className="cursor-pointer">
                    <Cloud className="mr-2 h-4 w-4 text-primary" />
                    Meine Wolken
                  </Link>
                </DropdownMenuItem>
                
                <DropdownMenuSeparator />
                
                {/* Einstellungen */}
                <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                  ⚙️ Einstellungen
                </DropdownMenuLabel>
                
                <DropdownMenuItem asChild>
                  <Link to="/portal/einstellungen?tab=zahlung" className="cursor-pointer">
                    <CreditCard className="mr-2 h-4 w-4 text-primary" />
                    Zahlungseinstellungen
                  </Link>
                </DropdownMenuItem>
                
                <DropdownMenuItem asChild>
                  <Link to="/portal/einstellungen?tab=benachrichtigungen" className="cursor-pointer">
                    <Bell className="mr-2 h-4 w-4 text-primary" />
                    Benachrichtigungen
                  </Link>
                </DropdownMenuItem>
                
                <DropdownMenuItem asChild>
                  <Link to="/portal/einstellungen?tab=sicherheit" className="cursor-pointer">
                    <Shield className="mr-2 h-4 w-4 text-primary" />
                    Sicherheit & Passwort
                  </Link>
                </DropdownMenuItem>
                
                <DropdownMenuSeparator />
                
                {/* Logout */}
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-destructive cursor-pointer focus:text-destructive focus:bg-destructive/10"
                  data-testid="logout-button"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Ausloggen
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="container mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-4">
          {/* Sidebar Navigation */}
          <aside className="lg:col-span-1">
            <Card className="glass-card p-4">
              <nav className="space-y-1" data-testid="portal-nav">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      isActive(item.path)
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                    data-testid={`portal-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </Link>
                ))}
                
                {/* Settings Link in Sidebar */}
                <div className="pt-4 mt-4 border-t">
                  <Link
                    to="/portal/einstellungen"
                    className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      isActive('/portal/einstellungen')
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                    data-testid="portal-nav-einstellungen"
                  >
                    <Settings className="h-4 w-4" />
                    Einstellungen
                  </Link>
                </div>
              </nav>

              {/* User Info Card */}
              <div className="mt-6 rounded-lg bg-gradient-to-r from-primary/10 to-blue-500/10 p-3 border border-primary/20">
                <p className="text-xs text-muted-foreground">Eingeloggt als</p>
                <p className="text-sm font-medium truncate">{user?.email}</p>
              </div>
            </Card>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-3">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};
